/**
 * AVI AI Tools - Master Integrations Controller
 * 1:1 Port from admin.kaywat
 * Handles account-wide OAuth services, category filtering, search,
 * and live lifecycle modals (Connect, Animated Handshake, Connected Stats, Disconnect).
 */

export interface AccountIntegrationRecord {
  serviceId: string;
  serviceName: string;
  accountEmail: string;
  connectedAt: string;
  syncAllBusinesses: boolean;
  stats?: Record<string, any>;
  token?: string;
}

export interface IntegrationServiceConfig {
  name: string;
  tag: string;
  desc: string;
  defaultEmail: string;
  perms: string[];
  defaultStats: Record<string, any>;
  statsFn: (record: Partial<AccountIntegrationRecord>) => string;
  synergyNote: string;
}

class AccountIntegrationsController {
  public activeCategory: string = 'all';
  public searchQuery: string = '';
  public currentServiceId: string = 'betterbase';
  private initialized: boolean = false;

  public static readonly ACTIVE_SERVICES = new Set<string>([
    'betterbase',
    'credentialhub',
    'justshop',
    'kaypay',
    'justopened',
    'github',
    'tiktok',
    'google-account',
    'google-business',
    'paypal',
    'stripe'
  ]);

  public static readonly SERVICE_CONFIGS: Record<string, IntegrationServiceConfig> = {
    betterbase: {
      name: 'BetterBase',
      tag: 'Database · Auth · IAM · Email',
      desc: 'Authenticate your BetterBase account via OAuth to link your organizations, projects, user authentication tables, IAM roles, and email templates across your master account.',
      defaultEmail: 'guest@ai-tools.com',
      perms: [
        'Organizations & Projects (read/write access)',
        'Authentication database & user tables schema',
        'IAM roles — admin, manager, staff, support, creative',
        'Email templates & master SMTP relay configuration',
        'Active session tokens & API keys'
      ],
      defaultStats: { orgs: 2, projects: 33, sessions: 5, roles: 'Active' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.projects ?? 33}</span><span class="kaywat-linked-lbl">Projects</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.orgs ?? 2}</span><span class="kaywat-linked-lbl">Organizations</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.sessions ?? 5}</span><span class="kaywat-linked-lbl">Session Tokens</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">Active</span><span class="kaywat-linked-lbl">IAM Schema</span></div>
      `,
      synergyNote: 'Master account database connected — auth tables, sessions, and organizations are synced across all your workspaces.'
    },
    credentialhub: {
      name: 'CredentialHub',
      tag: 'Credentials · Notes · Vault · Secrets',
      desc: 'Authenticate your CredentialHub account via OAuth to surface your saved credentials, business notes, session tokens, and IAM role assignments directly in your dashboard.',
      defaultEmail: 'guest@ai-tools.com',
      perms: [
        'Saved credentials & logins (read access)',
        'Business notes & secure annotations',
        'Session tokens & active login sessions',
        'IAM role assignments across workspaces',
        'API keys & environment secrets'
      ],
      defaultStats: { vaultItems: 48, notes: 12, sessions: 3, secrets: 16 },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.vaultItems ?? 48}</span><span class="kaywat-linked-lbl">Vault Items</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.notes ?? 12}</span><span class="kaywat-linked-lbl">Secure Notes</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.sessions ?? 3}</span><span class="kaywat-linked-lbl">Sessions</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.secrets ?? 16}</span><span class="kaywat-linked-lbl">API Secrets</span></div>
      `,
      synergyNote: 'Vault encryption active — secure credentials and session tokens are safeguarded across all your tools.'
    },
    justshop: {
      name: 'JustShop',
      tag: 'Storefronts · Orders · Revenue · Products',
      desc: 'Authenticate your JustShop merchant account via OAuth to sync storefronts, products, memberships, orders, and revenue analytics across all sales channels.',
      defaultEmail: 'guest@ai-tools.com',
      perms: [
        'Storefronts & shops (website, extension, mobile PWA)',
        'Products, memberships & one-off sales catalog',
        'Draft & completed orders + revenue tracking',
        'Checkout flow — final 1–2 screens on all sales channels',
        'Inventory management & invoice email notifications'
      ],
      defaultStats: { stores: 3, orders: 127, products: 24, checkout: 'Active' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.stores ?? 3}</span><span class="kaywat-linked-lbl">Stores</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.orders ?? 127}</span><span class="kaywat-linked-lbl">Orders</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.products ?? 24}</span><span class="kaywat-linked-lbl">Products</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">100%</span><span class="kaywat-linked-lbl">Checkout Rail</span></div>
      `,
      synergyNote: 'JustShop merchant account active — sales catalogs, storefront checkouts, and customer orders propagate smoothly.'
    },
    kaypay: {
      name: 'KayPay',
      tag: 'Crypto Gateway · 0.8% Fee (0% with $KAY)',
      desc: 'Authenticate your KayPay account via OAuth to accept non-custodial cryptocurrency payments across all checkouts. 0.8% fee for standard crypto and 0% fee with $KAY coin.',
      defaultEmail: 'merchant@kaypay.kaywat.codes',
      perms: [
        'Non-custodial merchant gateway setup & payout rail',
        '0.8% standard crypto settlement rail (BTC, ETH, SOL, USDC, USDT)',
        '0% fee $KAY Coin Arbitrum One smart contract integration',
        'Direct-to-wallet on-chain settlement & instant transaction verification',
        'Universal checkout widget embed & payment webhook signatures'
      ],
      defaultStats: { wallet: '0x3CD3...427b', volume: '$18,420', kayFee: '0.0%', stdFee: '0.8%' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="font-size: 0.95rem;">${d.stats?.wallet ?? '0x3CD3...427b'}</span><span class="kaywat-linked-lbl">Payout Wallet</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.volume ?? '$18,420'}</span><span class="kaywat-linked-lbl">Volume</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">0.0%</span><span class="kaywat-linked-lbl">$KAY Fee</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">0.8%</span><span class="kaywat-linked-lbl">Std Fee</span></div>
      `,
      synergyNote: 'Direct-to-wallet settlement active — 0% fee $KAY coin and 0.8% crypto payments are enabled.'
    },
    justopened: {
      name: 'JustOpened',
      tag: 'Email Marketing · Campaigns · Deliverability · CRM',
      desc: 'Authenticate your JustOpened account via OAuth to link email marketing campaigns, sync subscriber CRM lists, inspect outbox delivery and open/click telemetry, and trigger automated dispatches.',
      defaultEmail: 'guest@ai-tools.com',
      perms: [
        'Subscriber lists, tags & custom segmentation attributes',
        'Campaign templates & visual drag-and-drop editor states',
        'Outbox delivery relay & SMTP health telemetry',
        'Open rate, click tracking & bounce telemetry',
        'Automated welcome and onboarding sequence dispatchers'
      ],
      defaultStats: { lists: 4, subscribers: '8,450', openRate: '54.2%', outbox: 'Healthy' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.lists ?? 4}</span><span class="kaywat-linked-lbl">Lists</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.subscribers ?? '8,450'}</span><span class="kaywat-linked-lbl">Subscribers</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">${d.stats?.openRate ?? '54.2%'}</span><span class="kaywat-linked-lbl">Open Rate</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">Active</span><span class="kaywat-linked-lbl">Outbox</span></div>
      `,
      synergyNote: 'Email marketing engine linked — automated broadcasts, CRM subscriber lists, and open rates are synchronized.'
    },
    github: {
      name: 'GitHub',
      tag: 'Git · Repositories · CI/CD · OAuth 2.0',
      desc: 'Sign in with your GitHub account via OAuth to access repositories (public & private), branch commits, auto-discover web routes, and enable continuous Git deployment.',
      defaultEmail: 'developer@github.com',
      perms: [
        'Read and write repository code & commits',
        'Manage webhooks for automatic continuous integration',
        'Query branches, pull requests & releases',
        'Synchronize virtual workspace file systems'
      ],
      defaultStats: { repos: 14, branches: 28, commits: '1.2k', ci: 'Passing' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.repos ?? 14}</span><span class="kaywat-linked-lbl">Repositories</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.branches ?? 28}</span><span class="kaywat-linked-lbl">Branches</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.commits ?? '1.2k'}</span><span class="kaywat-linked-lbl">Commits</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">Passing</span><span class="kaywat-linked-lbl">CI / CD</span></div>
      `,
      synergyNote: 'GitHub repository sync active — automated workspace commits and continuous deployment enabled.'
    },
    paypal: {
      name: 'PayPal',
      tag: 'Payments · Subscriptions',
      desc: 'Connect PayPal for global checkout payments and recurring customer billing.',
      defaultEmail: 'merchant@paypal.com',
      perms: ['Process checkout orders', 'Manage recurring subscription billing', 'Issue refunds and dispute resolution'],
      defaultStats: { checkouts: 94, volume: '$4,120', status: 'Active', currency: 'USD' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.checkouts ?? 94}</span><span class="kaywat-linked-lbl">Checkouts</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.volume ?? '$4,120'}</span><span class="kaywat-linked-lbl">Volume</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">Active</span><span class="kaywat-linked-lbl">Gateway</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">USD</span><span class="kaywat-linked-lbl">Currency</span></div>
      `,
      synergyNote: 'PayPal checkout gateway enabled for global orders.'
    },
    stripe: {
      name: 'Stripe',
      tag: 'Card Payments · Invoices',
      desc: 'Connect Stripe for direct credit card payment processing, customer subscriptions, and automated invoices.',
      defaultEmail: 'billing@stripe.com',
      perms: ['Accept card payments', 'Manage customer billing subscriptions', 'Generate and send PDF invoices'],
      defaultStats: { charges: 312, volume: '$12,850', mrr: '$3,400', disputes: '0.0%' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.charges ?? 312}</span><span class="kaywat-linked-lbl">Charges</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.volume ?? '$12,850'}</span><span class="kaywat-linked-lbl">Volume</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.mrr ?? '$3,400'}</span><span class="kaywat-linked-lbl">MRR</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">0.0%</span><span class="kaywat-linked-lbl">Disputes</span></div>
      `,
      synergyNote: 'Stripe card processing active across all checkout endpoints.'
    },
    'google-account': {
      name: 'Google Account',
      tag: 'Google Workspace · SSO',
      desc: 'Sign in with Google to enable Single Sign-On, Drive sync, and Google Cloud operations.',
      defaultEmail: 'guest@ai-tools.com',
      perms: ['Single Sign-On authentication', 'Google Drive file backups', 'Google Cloud project access'],
      defaultStats: { sso: 'Enabled', driveSync: 'Ready', cloud: 'Active', scope: 'Standard' },
      statsFn: () => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">SSO</span><span class="kaywat-linked-lbl">Auth</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">Active</span><span class="kaywat-linked-lbl">Drive</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">Ready</span><span class="kaywat-linked-lbl">GCP API</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">Synced</span><span class="kaywat-linked-lbl">Profile</span></div>
      `,
      synergyNote: 'Google SSO and Cloud infrastructure synced.'
    },
    'google-business': {
      name: 'Google Business Profile',
      tag: 'Locations · Reviews',
      desc: 'Connect Google Business Profile to manage online listings, review responses, and business search presence.',
      defaultEmail: 'business@google.com',
      perms: ['Manage business location details', 'Read and reply to Google Reviews', 'Access local search analytics'],
      defaultStats: { locations: 2, reviews: 48, rating: '4.9 ★', views: '2.4k' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.locations ?? 2}</span><span class="kaywat-linked-lbl">Locations</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.reviews ?? 48}</span><span class="kaywat-linked-lbl">Reviews</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #fbbf24;">4.9 ★</span><span class="kaywat-linked-lbl">Rating</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">2.4k</span><span class="kaywat-linked-lbl">Views/mo</span></div>
      `,
      synergyNote: 'Google Business Profile synced for location search and reviews.'
    },
    tiktok: {
      name: 'TikTok',
      tag: 'Short Video · Ad Assets',
      desc: 'Link TikTok creator and business profile for automated marketing and short video distribution.',
      defaultEmail: 'creator@tiktok.com',
      perms: ['Publish video assets to TikTok account', 'Query creator engagement analytics', 'Manage TikTok Ads Manager integration'],
      defaultStats: { followers: '18.4k', views: '142k', videos: 19, engagement: '8.2%' },
      statsFn: (d) => `
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.followers ?? '18.4k'}</span><span class="kaywat-linked-lbl">Followers</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.views ?? '142k'}</span><span class="kaywat-linked-lbl">Total Views</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val">${d.stats?.videos ?? 19}</span><span class="kaywat-linked-lbl">Reels</span></div>
        <div class="kaywat-linked-stat"><span class="kaywat-linked-val" style="color: #10b981;">8.2%</span><span class="kaywat-linked-lbl">Engagement</span></div>
      `,
      synergyNote: 'TikTok marketing pipeline linked for video creation and analytics.'
    }
  };

  public init() {
    if (this.initialized) {
      this.initCardStatuses();
      return;
    }
    this.initialized = true;
    this.initCardStatuses();
    console.log('[Account Integrations] Controller Initialized 1:1 with admin.kaywat.');
  }

  public initCardStatuses() {
    try {
      const raw = localStorage.getItem('kaywat_integrations_state');
      const state: Record<string, any> = raw ? JSON.parse(raw) : {};

      // Seed GitHub as Connected by default (matches admin.kaywat screenshot)
      if (!state['github']) {
        state['github'] = {
          serviceId: 'github',
          serviceName: 'GitHub',
          accountEmail: 'itsKayWat@github.com',
          connectedAt: new Date().toISOString(),
          stats: { repos: 14, branches: 28, commits: '1.2k', ci: 'Passing' }
        };
        localStorage.setItem('kaywat_integrations_state', JSON.stringify(state));
      }

      Object.keys(state).forEach((svcId) => {
        const card = document.querySelector(`.integration-card[data-service-id="${svcId}"]`);
        if (card) {
          const statusEl = card.querySelector('.integration-card-status');
          const btnEl = card.querySelector('.integration-card-btn');
          if (statusEl) {
            statusEl.innerHTML = '<span style="color: #10b981; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;">✓ Connected</span>';
          }
          if (btnEl) {
            btnEl.textContent = 'Manage';
            btnEl.classList.add('connected');
          }
        }
      });
    } catch (_) {}
  }

  public toggleCategoryDropdown() {
    const menu = document.getElementById('integrationsDropdownMenu');
    if (menu) menu.classList.toggle('open');
  }

  public selectCategory(cat: string, label: string) {
    this.activeCategory = cat;
    const menu = document.getElementById('integrationsDropdownMenu');
    if (menu) menu.classList.remove('open');

    const cur = document.getElementById('integrationsDropdownCurrent');
    if (cur) cur.textContent = label;

    const heading = document.getElementById('integrationsCategoryHeading');
    if (heading) heading.textContent = label;

    const items = document.querySelectorAll('.integrations-dropdown-item');
    items.forEach((item) => {
      item.classList.toggle('active', item.getAttribute('data-category') === cat);
    });

    this.applyFilters();
  }

  public handleSearch(query: string) {
    this.searchQuery = (query || '').trim().toLowerCase();
    this.applyFilters();
  }

  private applyFilters() {
    const cat = this.activeCategory;
    const q = this.searchQuery;

    const cards = document.querySelectorAll('.integration-card');
    cards.forEach((el) => {
      const card = el as HTMLElement;
      const cardCat = (card.getAttribute('data-category') || '').toLowerCase();
      const cardName = (card.getAttribute('data-name') || '').toLowerCase();
      const cardSvc = (card.getAttribute('data-service-id') || '').toLowerCase();

      const catMatches = cat === 'all' || cardCat.includes(cat.toLowerCase());
      const queryMatches = !q || cardName.includes(q) || cardSvc.includes(q);

      card.style.display = catMatches && queryMatches ? 'flex' : 'none';
    });

    // Hide divider if searching
    const divider = document.getElementById('upcomingIntegrationsDivider');
    if (divider) {
      divider.style.display = q || cat !== 'all' ? 'none' : 'flex';
    }
  }

  public openConnectModal(serviceId: string, serviceName?: string) {
    serviceId = (serviceId || 'betterbase').toLowerCase();
    this.currentServiceId = serviceId;

    const modal = document.getElementById('oauthModal');
    if (!modal) return;

    const cfg = AccountIntegrationsController.SERVICE_CONFIGS[serviceId] || {
      name: serviceName || serviceId,
      tag: 'OAuth 2.0 Integration',
      desc: `Authenticate your ${serviceName || serviceId} account via OAuth to link operations.`,
      defaultEmail: 'guest@ai-tools.com',
      perms: ['Basic profile access', 'Read workspace resources', 'Synchronize operational telemetry'],
      defaultStats: {},
      statsFn: () => '<div class="kaywat-linked-stat"><span class="kaywat-linked-val">Active</span><span class="kaywat-linked-lbl">Status</span></div>',
      synergyNote: 'Integration connected and active.'
    };

    // Copy Logo SVG from card
    const card = document.querySelector(`.integration-card[data-service-id="${serviceId}"]`);
    const cardLogoWrap = card?.querySelector('.integration-card-logo-wrap');
    const logoContainer = document.getElementById('oauthAppLogo');
    const loadingLogo = document.getElementById('oauthLoadingLogo');
    if (cardLogoWrap && logoContainer) logoContainer.innerHTML = cardLogoWrap.innerHTML;
    if (cardLogoWrap && loadingLogo) loadingLogo.innerHTML = cardLogoWrap.innerHTML;

    // Check if connected
    let isConnected = false;
    let savedRecord: any = null;
    try {
      const raw = localStorage.getItem('kaywat_integrations_state');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed[serviceId]) {
          isConnected = true;
          savedRecord = parsed[serviceId];
        }
      }
    } catch (_) {}

    const stateSignIn = document.getElementById('oauthStateSignIn');
    const stateConnecting = document.getElementById('oauthStateConnecting');
    const stateConnected = document.getElementById('oauthStateConnected');

    if (stateConnecting) stateConnecting.style.display = 'none';

    if (isConnected) {
      // Show Connected State
      if (stateSignIn) stateSignIn.style.display = 'none';
      if (stateConnected) stateConnected.style.display = 'block';

      const titleEl = document.getElementById('oauthSuccessTitle');
      const emailEl = document.getElementById('oauthSuccessEmail');
      const statsEl = document.getElementById('oauthLinkedData');

      if (titleEl) titleEl.textContent = `${cfg.name} Connected`;
      if (emailEl) emailEl.textContent = savedRecord?.accountEmail || cfg.defaultEmail;
      if (statsEl) statsEl.innerHTML = cfg.statsFn(savedRecord || { stats: cfg.defaultStats });
    } else {
      // Show Sign In / Consent State
      if (stateConnected) stateConnected.style.display = 'none';
      if (stateSignIn) stateSignIn.style.display = 'block';

      const nameEl = document.getElementById('oauthAppName');
      const tagEl = document.getElementById('oauthAppTag');
      const descEl = document.getElementById('oauthAppDesc');
      const permsList = document.getElementById('oauthPermsList');

      if (nameEl) nameEl.textContent = cfg.name;
      if (tagEl) tagEl.textContent = cfg.tag;
      if (descEl) descEl.innerHTML = `<strong>AVI AI Tools</strong> is requesting authorization to connect with your <span>${cfg.name}</span> account.`;

      if (permsList) {
        permsList.innerHTML = cfg.perms.map(p => `<li>${p}</li>`).join('');
      }
    }

    modal.classList.add('active');
  }

  public closeOAuthModal() {
    const modal = document.getElementById('oauthModal');
    if (modal) modal.classList.remove('active');
  }

  public handleApproveOAuth() {
    const svcId = this.currentServiceId;
    const cfg = AccountIntegrationsController.SERVICE_CONFIGS[svcId];
    if (!cfg) return;

    const stateSignIn = document.getElementById('oauthStateSignIn');
    const stateConnecting = document.getElementById('oauthStateConnecting');
    const stateConnected = document.getElementById('oauthStateConnected');

    if (stateSignIn) stateSignIn.style.display = 'none';
    if (stateConnecting) stateConnecting.style.display = 'block';

    const step1 = document.getElementById('oauthStep1');
    const step2 = document.getElementById('oauthStep2');
    const step3 = document.getElementById('oauthStep3');

    if (step1) { step1.className = 'kaywat-oauth-step active'; }
    if (step2) { step2.className = 'kaywat-oauth-step'; }
    if (step3) { step3.className = 'kaywat-oauth-step'; }

    setTimeout(() => {
      if (step1) { step1.className = 'kaywat-oauth-step done'; }
      if (step2) { step2.className = 'kaywat-oauth-step active'; }
    }, 600);

    setTimeout(() => {
      if (step2) { step2.className = 'kaywat-oauth-step done'; }
      if (step3) { step3.className = 'kaywat-oauth-step active'; }
    }, 1200);

    setTimeout(() => {
      if (step3) { step3.className = 'kaywat-oauth-step done'; }

      // Save to localStorage
      try {
        const raw = localStorage.getItem('kaywat_integrations_state') || '{}';
        const state = JSON.parse(raw);
        state[svcId] = {
          serviceId: svcId,
          serviceName: cfg.name,
          accountEmail: cfg.defaultEmail,
          connectedAt: new Date().toISOString(),
          stats: cfg.defaultStats
        };
        localStorage.setItem('kaywat_integrations_state', JSON.stringify(state));
      } catch (_) {}

      // Update card in DOM
      const card = document.querySelector(`.integration-card[data-service-id="${svcId}"]`);
      if (card) {
        const statusEl = card.querySelector('.integration-card-status');
        const btnEl = card.querySelector('.integration-card-btn');
        if (statusEl) {
          statusEl.innerHTML = '<span style="color: #10b981; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;">✓ Connected</span>';
        }
        if (btnEl) {
          btnEl.textContent = 'Manage';
          btnEl.classList.add('connected');
        }
      }

      if (stateConnecting) stateConnecting.style.display = 'none';
      if (stateConnected) stateConnected.style.display = 'block';

      const titleEl = document.getElementById('oauthSuccessTitle');
      const emailEl = document.getElementById('oauthSuccessEmail');
      const statsEl = document.getElementById('oauthLinkedData');

      if (titleEl) titleEl.textContent = `${cfg.name} Connected`;
      if (emailEl) emailEl.textContent = cfg.defaultEmail;
      if (statsEl) statsEl.innerHTML = cfg.statsFn({ stats: cfg.defaultStats });

      this.toast(`Successfully connected ${cfg.name}!`, 'success');
    }, 1800);
  }

  public handleDisconnectService() {
    const svcId = this.currentServiceId;
    const cfg = AccountIntegrationsController.SERVICE_CONFIGS[svcId];

    try {
      const raw = localStorage.getItem('kaywat_integrations_state');
      if (raw) {
        const state = JSON.parse(raw);
        delete state[svcId];
        localStorage.setItem('kaywat_integrations_state', JSON.stringify(state));
      }
    } catch (_) {}

    const card = document.querySelector(`.integration-card[data-service-id="${svcId}"]`);
    if (card) {
      const statusEl = card.querySelector('.integration-card-status');
      const btnEl = card.querySelector('.integration-card-btn');
      if (statusEl) statusEl.textContent = 'Not Integrated';
      if (btnEl) {
        btnEl.textContent = 'Integrate';
        btnEl.classList.remove('connected');
      }
    }

    this.closeOAuthModal();
    this.toast(`Disconnected ${cfg?.name || svcId}`, 'info');
  }

  public openHelpModal() {
    alert('Account-Wide Integrations: Connect global webhooks, databases, payment gateways, and OAuth services across all your AVI AI Tools workspaces.');
  }

  public toast(msg: string, type: 'success' | 'error' | 'info' = 'info') {
    let toast = document.getElementById('obsToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'obsToast';
      toast.className = 'obs-toast';
      document.body.appendChild(toast);
    }

    toast.className = `obs-toast ${type} show`;
    toast.innerHTML = `<i class="fas fa-${type === 'success' ? 'check-circle' : (type === 'error' ? 'exclamation-circle' : 'info-circle')}"></i> ${msg}`;

    setTimeout(() => {
      if (toast) toast.classList.remove('show');
    }, 2800);
  }
}

// Singleton Export
export const accountIntegrationsController = new AccountIntegrationsController();
(window as any).accountIntegrations = accountIntegrationsController;

// Window exports for onclick handlers in HTML
(window as any).openConnectModal = (id: string, name?: string) => accountIntegrationsController.openConnectModal(id, name);
(window as any).openOAuthModal = (id: string, name?: string) => accountIntegrationsController.openConnectModal(id, name);
(window as any).closeOAuthModal = () => accountIntegrationsController.closeOAuthModal();
(window as any).handleApproveOAuth = () => accountIntegrationsController.handleApproveOAuth();
(window as any).handleDisconnectService = () => accountIntegrationsController.handleDisconnectService();
(window as any).toggleCategoryDropdown = () => accountIntegrationsController.toggleCategoryDropdown();
(window as any).selectCategory = (cat: string, label: string) => accountIntegrationsController.selectCategory(cat, label);
(window as any).handleSearch = (query: string) => accountIntegrationsController.handleSearch(query);
(window as any).openHelpModal = () => accountIntegrationsController.openHelpModal();
(window as any).initIntegrations = () => accountIntegrationsController.init();
